import React, { PropsWithChildren, useRef } from 'react';
import classNames from 'classnames';
import cardStyles from '~/components/atoms/card/card.module.scss';
import Loader from '~/components/atoms/loader/Loader';
import { useConfig } from '~/core/configuration/configContext';
import { Configuration } from '~/core/configuration/configuration.type';
import { ResultProduct } from '~/core/results/types/results.type';
import { useSearchQuery } from '~/core/state/searchQueryContext';
import { useSERPState } from '~/core/state/SERPStateContext';
import { GoToResultHandler } from '~/hooks/useGoToResult';
import { usePlugin } from '~/plugins/pluginContext';
import { ProductContextProvider, Style } from './productResultContext';

export interface CustomProductColorsConfiguration {
    labels: Configuration['layout']['configuration']['labels'];
    deliveries: Configuration['layout']['configuration']['deliveries'];
}

export interface ProductSingleResultContainerProps extends PropsWithChildren {
    handleGoToResult: GoToResultHandler;
    order: number;
    result: ResultProduct;
    customColorsConfiguration: CustomProductColorsConfiguration;
    productStyles: Style;
    isInstantSearch?: boolean;
}

const ProductResultContainer: React.FC<ProductSingleResultContainerProps> = (props) => {
    const { customColorsConfiguration, handleGoToResult, order, children, productStyles, isInstantSearch } = props;
    const { doAction, applyFilter } = usePlugin();
    const config = useConfig();
    const state = useSERPState();
    const result = applyFilter<ResultProduct, ResultProduct>('beforeProductRender', props.result, { config });
    const { searchQuery } = useSearchQuery();
    doAction('beforeProductRender', { product: result, searchQuery });
    const propsAfterFilter = { ...props, result };
    const linkElementRef = useRef<HTMLDivElement>(null);

    const linkElement = useRef<HTMLDivElement>(null);

    return (
        <ProductContextProvider
            result={propsAfterFilter.result}
            linkElementRef={linkElementRef}
            customColorsConfiguration={customColorsConfiguration}
            style={productStyles}
        >
            <React.Suspense fallback={<Loader />}>
                <article
                    className={classNames(
                        cardStyles.serpCard,
                        cardStyles.serpCardPrimary,
                        productStyles.serpProduct,
                        state.resultLayout === 'rows' ? productStyles.inRows : '',
                        productStyles.serpProductLink,
                        isInstantSearch && result.isTopProduct ? productStyles.instantProductResultTop : ''
                    )}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        handleGoToResult({
                            url: result.url,
                            order: order,
                            resultType: 'product',
                            openInNewTab: e.button === 1 || (e.button === 0 && e.ctrlKey),
                            button: e.button,
                            key: e.ctrlKey ? 'ctrl' : undefined,
                        });
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target === linkElement.current) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGoToResult({
                                url: result.url,
                                order,
                                resultType: 'product',
                                key: 'Enter',
                            });
                        }
                    }}
                    role="presentation"
                    data-testid="product-result-card"
                >
                    {children}
                </article>
            </React.Suspense>
        </ProductContextProvider>
    );
};

export default ProductResultContainer;
